/**
 * Utility functions for extracting and formatting error messages from API responses.
 */

const TECHNICAL_OBJECT_ID_PATTERNS = [
  'invalid source_social_media_post_id',
  '24 character hex string',
  '12 byte uint8array',
] as const;

/**
 * Maps raw backend validation strings to user-safe copy (e.g. Mongo ObjectId cast leaks).
 */
export function sanitizeApiErrorMessageForDisplay(message: string): string {
  const lower = message.toLowerCase();
  if (TECHNICAL_OBJECT_ID_PATTERNS.some((fragment) => lower.includes(fragment))) {
    return 'Could not save right now. Please try again.';
  }
  return message;
}

/**
 * Extracts a user-friendly error message from an axios error or generic error.
 * 
 * @param error - The error object (can be axios error, Error, or unknown type)
 * @returns User-friendly error message string
 */
export function getErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred';
  }

  // Handle axios errors
  if (typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: {
          message?: string;
          error?: string;
          errors?: string[] | Record<string, string[]>;
        };
      };
      message?: string;
    };

    if (axiosError.response?.data) {
      const data = axiosError.response.data;
      
      // Try to extract message from various possible fields
      if (data.message) {
        return sanitizeApiErrorMessageForDisplay(data.message);
      }
      if (data.error) {
        return sanitizeApiErrorMessageForDisplay(data.error);
      }
      
      // Handle validation errors (array or object format)
      if (data.errors) {
        if (Array.isArray(data.errors)) {
          return sanitizeApiErrorMessageForDisplay(data.errors.join(', '));
        }
        if (typeof data.errors === 'object') {
          const messages = Object.values(data.errors).flat();
          return sanitizeApiErrorMessageForDisplay(messages.join(', '));
        }
      }
      
      // If we have a status code, provide a generic message
      if (axiosError.response.status) {
        return `Server error (${axiosError.response.status})`;
      }
    }
    
    // Check for network errors
    if ('request' in error && !('response' in error)) {
      return 'Network error - please check your connection';
    }
    
    // Fall back to error message if available
    if (axiosError.message) {
      return sanitizeApiErrorMessageForDisplay(axiosError.message);
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return sanitizeApiErrorMessageForDisplay(error.message);
  }

  // Handle string errors
  if (typeof error === 'string') {
    return sanitizeApiErrorMessageForDisplay(error);
  }

  // Fallback
  return 'An unexpected error occurred';
}

/**
 * Extracts detailed error information for logging.
 * 
 * @param error - The error object
 * @returns Object with detailed error information
 */
export function getErrorDetails(error: unknown): {
  message: string;
  status?: number;
  mode?: string;
  context?: Record<string, unknown>;
} {
  const message = getErrorMessage(error);
  const details: ReturnType<typeof getErrorDetails> = { message };

  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: unknown;
      };
    };
    
    if (axiosError.response) {
      details.status = axiosError.response.status;
      if (axiosError.response.data) {
        details.context = { responseData: axiosError.response.data };
      }
    }
  }

  return details;
}

/**
 * True for 401/403 API responses, i.e. the endpoint wants a signed-in user.
 */
export function isAuthRequiredError(error: unknown): boolean {
  const { status } = getErrorDetails(error);
  return status === 401 || status === 403;
}
